/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import { VisuallyHidden } from '@coursera/cds-core';

import ListBody from 'bundles/discussions/components/ListBody';
import GradedDiscussionPromptAnswersSortBar from 'bundles/discussions/components/repliesList/GradedDiscussionPromptAnswersSortBar';
import RepliesSortBar, { generateTabId } from 'bundles/discussions/components/repliesList/RepliesSortBar';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import ReplyListItemContainer from 'bundles/discussions/components/repliesList/ReplyListItemContainer';
import { loadingStates, naptimeForumTypes, savingStates as savingStatesConstant } from 'bundles/discussions/constants';
import PaginationControls from 'bundles/page/components/PaginationControls';

import _t from 'i18n!nls/discussions';

const styles = {
  replyListWrapper: css`
    padding: var(--cds-spacing-150) 0;
  `,
};

class RepliesList extends React.Component {
  static propTypes = {
    sort: PropTypes.string.isRequired,
    page: PropTypes.number.isRequired,
    savingStates: PropTypes.object.isRequired,
    replies: PropTypes.array.isRequired,
    loadingState: PropTypes.string.isRequired,
    isQuestionLoaded: PropTypes.bool,
    answerId: PropTypes.string,
    commentId: PropTypes.string,
    pageCount: PropTypes.number,
    fetchAnswerPosition: PropTypes.func,

    // Only coming in via DiscussionPrompts
    forumLink: PropTypes.string,
    forumType: PropTypes.string,
    forumTypeSetting: PropTypes.string,
    isReadOnlyAfterSessionEnd: PropTypes.bool,
  };

  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof RepliesList.contextTypes>;

  state = {
    focusOnReplyId: undefined,
    announceDeleteSuccessNotification: false,
  };

  onPageChange = (page: $TSFixMe) => {
    this.context.router.push({
      pathname: this.context.router.location.pathname,
      params: this.context.router.params,
      query: Object.assign(this.context.router.location.query, { page }),
    });
  };

  handleDeleteClick = () => {
    this.setState({
      announceDeleteSuccessNotification: false,
    });
  };

  handleDeleteSuccess = () => {
    this.setState({
      announceDeleteSuccessNotification: true,
    });
  };

  componentDidUpdate(prevProps: $TSFixMe) {
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'sort' does not exist on type 'Readonly<{... Remove this comment to see the full error message
    const { replies, savingStates, questionId } = this.props;
    // savingState is empty when page loads and becomes populated when a new reply is posted
    const isNewReply = savingStates[questionId] === savingStatesConstant.SAVED;

    // focus the latest reply anytime the number of replies increases. replies are sorted by createdAt in descending order
    if (isNewReply && (prevProps?.replies?.length ?? 0) < replies?.length) {
      this.setState({
        focusOnReplyId: replies[0].id,
      });
    }
  }

  render() {
    const {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'sort' does not exist on type 'Readonly<{... Remove this comment to see the full error message
      sort,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'page' does not exist on type 'Readonly<{... Remove this comment to see the full error message
      page,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'replies' does not exist on type 'Readonl... Remove this comment to see the full error message
      replies,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'answerId' does not exist on type 'Readon... Remove this comment to see the full error message
      answerId,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'commentId' does not exist on type 'Reado... Remove this comment to see the full error message
      commentId,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'forumLink' does not exist on type 'Reado... Remove this comment to see the full error message
      forumLink,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'forumType' does not exist on type 'Reado... Remove this comment to see the full error message
      forumType,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'isQuestionLoaded' does not exist on type... Remove this comment to see the full error message
      isQuestionLoaded,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'loadingState' does not exist on type 'Re... Remove this comment to see the full error message
      loadingState,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'savingStates' does not exist on type 'Re... Remove this comment to see the full error message
      savingStates,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'pageCount' does not exist on type 'Reado... Remove this comment to see the full error message
      pageCount,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'forumTypeSetting' does not exist on type... Remove this comment to see the full error message
      forumTypeSetting,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'fetchAnswerPosition' does not exist on t... Remove this comment to see the full error message
      fetchAnswerPosition,
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'isReadOnlyAfterSessionEnd' does not exist on t... Remove this comment to see the full error message
      isReadOnlyAfterSessionEnd,
    } = this.props;

    const isLoaded = loadingState === loadingStates.DONE;

    const isGradedDiscussionPrompt = forumType === naptimeForumTypes.gradedDiscussionPrompt;

    return (
      <div>
        <div className="rc-RepliesList card-rich-interaction nostyle">
          <RepliesSortBar sort={sort} forumType={forumType} forumTypeSetting={forumTypeSetting} />

          {/* This id is used in RepliesSortBar to link the tabs to the tabpanel */}
          <div
            role="tabpanel"
            id="replies-list-tabpanel"
            aria-labelledby={generateTabId(isGradedDiscussionPrompt ? forumTypeSetting : sort)}
            css={styles.replyListWrapper}
          >
            {isGradedDiscussionPrompt && (
              <GradedDiscussionPromptAnswersSortBar sort={sort} loadingState={loadingState} />
            )}

            <ListBody loadingState={loadingState} emptyStatePlaceholderText={_t('No Replies Yet')}>
              <VisuallyHidden role="status" component="span" className="rc-A11yScreenReaderOnly">
                {this.state.announceDeleteSuccessNotification && _t('Reply deleted successfully.')}
              </VisuallyHidden>
              {replies &&
                replies.map((reply: $TSFixMe) => (
                  <ReplyListItemContainer
                    // @ts-expect-error ts-migrate(2322) FIXME: Type '{ reply: any; answerId: any; commentId: any;... Remove this comment to see the full error message
                    reply={reply}
                    answerId={answerId}
                    commentId={commentId}
                    forumLink={forumLink}
                    forumType={forumType}
                    isQuestionLoaded={isQuestionLoaded}
                    savingState={savingStates[reply.id]}
                    key={reply.id}
                    fetchAnswerPosition={fetchAnswerPosition}
                    isFocused={reply.id === this.state.focusOnReplyId}
                    onDeleteClick={this.handleDeleteClick}
                    onDeleteSuccess={this.handleDeleteSuccess}
                    isReadOnlyAfterSessionEnd={isReadOnlyAfterSessionEnd}
                  />
                ))}
            </ListBody>
          </div>
        </div>

        {isLoaded && !!pageCount && (
          <PaginationControls maxPages={5} currentPage={page} pageCount={pageCount} onPageChange={this.onPageChange} />
        )}
      </div>
    );
  }
}

export default connectToStores(
  RepliesList,
  ['ThreadDetailsStore'],
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'questionId' does not exist on type '{}'.
  ({ ThreadDetailsStore }, { questionId, page, sort }) => {
    return {
      replies: ThreadDetailsStore.getReplies(questionId, page, sort) || [],
      pageCount: ThreadDetailsStore.getPageCount(questionId, page, sort),
      savingStates: ThreadDetailsStore.savingStates,
      loadingState:
        ThreadDetailsStore.getAnswerLoadedState({
          questionId,
          page,
          sort,
        }) || loadingStates.LOADING,
      isQuestionLoaded: ThreadDetailsStore.isLoaded({ questionId, page, sort }),
    };
  }
);
